import React from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  Grid,
  useTheme,
  alpha,
  Chip,
  Card,
  CardMedia
} from '@mui/material';
import {
  LocalMall as LocalMallIcon,
  Palette as PaletteIcon,
  TrendingUp as TrendingUpIcon,
  Campaign as CampaignIcon,
  Analytics as AnalyticsIcon,
  Group as GroupIcon,
  Star as StarIcon,
  Timeline as TimelineIcon,
  Visibility as VisibilityIcon,
  Speed as SpeedIcon,
  TrendingUp as GrowthIcon,
  Loyalty as LoyaltyIcon,
  Grade as GradeIcon,
  Public as PublicIcon,
  Launch as LaunchIcon
} from '@mui/icons-material';
import Navigation from '../../Navigation';
import Footer from '../../Footer';

// Import brand image
import brandImage from '../assests/brands-page/brand.png';

const BrandsLandingPage = ({ onNavigateToAuth }) => {
  const theme = useTheme();

  // Hero Section Component
  const HeroSection = () => (
    <Box
      sx={{
        pt: { xs: 10, md: 8 },
        pb: { xs: 8, md: 12 },
        mt: { xs: 0, md: -10 },
        backgroundColor: '#ffffff',
        textAlign: 'center',
        minHeight: { xs: '80vh', md: '85vh' },
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Background Design Elements */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 0,
        opacity: 0.1
      }}>
        <Box sx={{
          position: 'absolute',
          top: '20%',
          right: '10%',
          width: { xs: 150, md: 200 },
          height: { xs: 150, md: 200 },
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #8b5cf6, #a855f7)',
          animation: 'float 7s ease-in-out infinite'
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: '30%',
          left: '15%',
          width: { xs: 120, md: 150 },
          height: { xs: 120, md: 150 },
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #7c3aed, #8b5cf6)',
          animation: 'float 9s ease-in-out infinite reverse'
        }} />
        <Box sx={{
          position: 'absolute',
          top: '60%',
          right: '25%',
          width: { xs: 100, md: 120 },
          height: { xs: 100, md: 120 },
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #2563eb, #3b82f6)',
          animation: 'float 5s ease-in-out infinite'
        }} />
        <style>
          {`
            @keyframes float {
              0%, 100% { transform: translateY(0px) rotate(0deg); }
              50% { transform: translateY(-25px) rotate(5deg); }
            }
          `}
        </style>
      </Box>

      <Box sx={{ maxWidth: '850px', mx: 'auto', px: { xs: 2, md: 3 }, position: 'relative', zIndex: 1 }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { xs: '1.75rem', md: '4.2rem' },
            fontWeight: 400,
            color: '#2d3748',
            mb: { xs: 2.5, md: 4 },
            lineHeight: 1.1
          }}
        >
          Build Powerful Brands with{' '}
          <Box 
            component="span" 
            sx={{ 
              backgroundColor: '#8b5cf6',
              color: '#ffffff',
              px: { xs: 2, md: 3 },
              py: { xs: 0.75, md: 1 },
              borderRadius: { xs: 2, md: 3 },
              display: 'inline-block',
              fontWeight: 500
            }}
          >
            Smart Management
          </Box>
        </Typography>
        <Typography
          variant="h5"
          sx={{
            fontSize: { xs: '1rem', md: '1.5rem' },
            fontWeight: 400,
            color: '#718096',
            mb: { xs: 3, md: 5 },
            fontStyle: 'italic',
            px: { xs: 1, md: 0 }
          }}
        >
          Create, manage, and grow your brand portfolio with comprehensive tools and insights!
        </Typography>
        <Stack direction="row" spacing={{ xs: 1.5, md: 2 }} justifyContent="center">
          <Button
            variant="contained"
            size="large"
            onClick={onNavigateToAuth}
            sx={{
              backgroundColor: '#8b5cf6',
              color: '#ffffff',
              fontWeight: 500,
              px: { xs: 3, md: 5 },
              py: { xs: 1.5, md: 2 },
              textTransform: 'none',
              fontSize: { xs: '0.95rem', md: '1.1rem' },
              borderRadius: { xs: 2, md: 3 },
              '&:hover': {
                backgroundColor: '#7c3aed'
              }
            }}
          >
            Start Building Today
          </Button>
        </Stack>
        <Typography
          variant="body2"
          sx={{
            color: '#9ca3af',
            mt: { xs: 2.5, md: 4 },
            fontSize: { xs: '0.8rem', md: '0.9rem' }
          }}
        >
          Instant setup - Begin managing your brands immediately
        </Typography>
      </Box>
    </Box>
  );

  // Scrolling Tags Component
  const ScrollingTags = () => (
    <Box sx={{ 
      width: '100%', 
      py: { xs: 2, md: 3 },
      mt: { xs: -8, md: -20 },
      bgcolor: alpha(theme.palette.grey[50], 0.8),
      overflow: 'hidden',
      position: 'relative'
    }}>
      <Box sx={{
        display: 'flex',
        gap: { xs: 2, md: 3 },
        animation: { xs: 'scroll 40s linear infinite', md: 'scroll 65s linear infinite' },
        width: 'max-content',
        '@keyframes scroll': {
          '0%': {
            transform: 'translateX(0)'
          },
          '100%': {
            transform: 'translateX(-50%)'
          }
        }
      }}>
        {[
          'Brand Identity',
          'Logo Design',
          'Brand Guidelines',
          'Brand Strategy',
          'Brand Portfolio',
          'Brand Analytics',
          'Brand Positioning',
          'Brand Awareness',
          'Brand Loyalty',
          'Brand Equity',
          'Brand Recognition',
          'Brand Management',
          'Brand Development',
          'Brand Marketing',
          'Brand Experience',
          'Brand Consistency',
          'Brand Messaging',
          'Brand Story',
          'Brand Values',
          'Brand Vision',
          'Brand Identity',
          'Logo Design',
          'Brand Guidelines',
          'Brand Strategy',
          'Brand Portfolio',
          'Brand Analytics',
          'Brand Positioning',
          'Brand Awareness',
          'Brand Loyalty',
          'Brand Equity',
          'Brand Recognition',
          'Brand Management',
          'Brand Development',
          'Brand Marketing',
          'Brand Experience',
          'Brand Consistency',
          'Brand Messaging',
          'Brand Story',
          'Brand Values',
          'Brand Vision'
        ].map((tag, index) => (
          <Chip
            key={index}
            label={tag}
            sx={{
              fontSize: { xs: '0.85rem', md: '1rem' },
              fontWeight: 500,
              px: { xs: 2, md: 3 },
              py: { xs: 1, md: 1.5 },
              height: 'auto',
              borderRadius: { xs: 2, md: 3 },
              bgcolor: 'white',
              color: 'text.primary',
              border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
              boxShadow: `0 4px 12px ${alpha(theme.palette.grey[900], 0.08)}`,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              whiteSpace: 'nowrap',
              cursor: 'default',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 8px 20px ${alpha('#8b5cf6', 0.15)}`,
                bgcolor: alpha('#8b5cf6', 0.05),
                borderColor: alpha('#8b5cf6', 0.3),
                color: '#8b5cf6'
              }
            }}
          />
        ))}
      </Box>
      
      {/* Gradient overlays for smooth fade effect */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: { xs: 60, md: 100 },
        height: '100%',
        background: `linear-gradient(to right, ${alpha(theme.palette.grey[50], 0.8)}, transparent)`,
        pointerEvents: 'none',
        zIndex: 1
      }} />
      <Box sx={{
        position: 'absolute',
        top: 0,
        right: 0,
        width: { xs: 60, md: 100 },
        height: '100%',
        background: `linear-gradient(to left, ${alpha(theme.palette.grey[50], 0.8)}, transparent)`,
        pointerEvents: 'none',
        zIndex: 1
      }} />
    </Box>
  );

  // Features Overview Component - Zigzag Layout
  const FeaturesOverview = () => {
    const mainFeatures = [
      {
        icon: <LocalMallIcon sx={{ fontSize: 60, color: '#ffffff' }} />,
        title: 'Brand Portfolio Management',
        description: 'Organize and manage all your brands in one centralized platform with detailed brand information, priority settings, and comprehensive tracking.',
        color: '#8b5cf6',
        items: ['Brand hierarchy', 'Priority ratings', 'Brand details', 'Portfolio overview']
      },
      {
        icon: <AnalyticsIcon sx={{ fontSize: 60, color: '#ffffff' }} />,
        title: 'Brand Analytics & Insights',
        description: 'Track brand performance with detailed analytics, market positioning insights, and data-driven recommendations for brand growth.',
        color: '#7c3aed',
        items: ['Performance metrics', 'Market analysis', 'Growth tracking', 'Competitive insights']
      },
      {
        icon: <CampaignIcon sx={{ fontSize: 60, color: '#ffffff' }} />,
        title: 'Brand Strategy & Positioning',
        description: 'Develop comprehensive brand strategies with positioning tools, brand guidelines, and marketing campaign management capabilities.',
        color: '#a855f7',
        items: ['Strategic planning', 'Brand positioning', 'Marketing campaigns', 'Brand guidelines']
      }
    ];

    return (
      <Box sx={{ py: 8, mt: 20 }}>
        <Box sx={{ px: { xs: 2, md: 0 } }}>
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              mb: 8,
              fontSize: { xs: '1.75rem', md: '3.2rem' },
              fontWeight: 300,
              color: '#1e293b',
              maxWidth: '800px',
              mx: 'auto'
            }}
          >
            Everything You Need for{' '}
            <Box component="span" sx={{ fontWeight: 500, color: '#8b5cf6' }}>Successful Brands</Box>
          </Typography>
        </Box>
        
        {mainFeatures.map((feature, index) => (
          <Box 
            key={index}
            sx={{ 
              py: 6,
              backgroundColor: index % 2 === 0 ? 'transparent' : alpha('#f8fafc', 0.7)
            }}
          >
            <Box sx={{ 
              maxWidth: '1300px', 
              mx: 'auto', 
              px: { xs: 2, md: 4 },
              display: 'flex',
              flexDirection: { xs: 'column', md: index % 2 === 0 ? 'row' : 'row-reverse' },
              gap: { xs: 3, md: 6 },
              alignItems: 'center'
            }}>
              <Box sx={{ flex: 1, width: '100%' }}>
                <Box sx={{ 
                  p: { xs: 3, md: 6 },
                  backgroundColor: feature.color,
                  borderRadius: { xs: 3, md: 6 },
                  color: 'white',
                  position: 'relative',
                  overflow: 'hidden',
                  width: '100%',
                  boxShadow: `0 20px 60px ${alpha(feature.color, 0.3)}`
                }}>
                  <Box sx={{ 
                    position: 'absolute',
                    top: -20,
                    right: -20,
                    width: { xs: 80, md: 120 },
                    height: { xs: 80, md: 120 },
                    borderRadius: '50%',
                    backgroundColor: alpha('#ffffff', 0.1)
                  }} />
                  <Box sx={{ mb: { xs: 2, md: 3 } }}>
                    {feature.icon}
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 500, mb: { xs: 2, md: 3 }, fontSize: { xs: '1.3rem', md: '1.8rem' } }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body1" sx={{ fontSize: { xs: '0.95rem', md: '1.1rem' }, lineHeight: 1.6, mb: { xs: 2.5, md: 4 }, opacity: 0.9 }}>
                    {feature.description}
                  </Typography>
                  <Stack spacing={{ xs: 0.75, md: 1 }}>
                    {feature.items.map((item, idx) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ 
                          width: { xs: 6, md: 8 }, 
                          height: { xs: 6, md: 8 }, 
                          borderRadius: '50%', 
                          backgroundColor: '#ffffff', 
                          mr: { xs: 1.5, md: 2 }
                        }} />
                        <Typography variant="body2" sx={{ fontSize: { xs: '0.9rem', md: '1rem' } }}>
                          {item}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </Box>
              
              <Box sx={{ flex: 1, width: '100%' }}>
                <Box sx={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  gap: { xs: 2, md: 3 }
                }}>
                  {/* Unique feature highlights for each section */}
                  {index === 0 && (
                    <>
                      <Box sx={{ 
                        p: { xs: 3, md: 4 }, 
                        backgroundColor: '#ffffff',
                        borderRadius: { xs: 3, md: 4 },
                        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                        border: '1px solid #e2e8f0',
                        textAlign: 'center'
                      }}>
                        <StarIcon sx={{ fontSize: { xs: 32, md: 40 }, color: feature.color, mb: { xs: 1.5, md: 2 } }} />
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 1, fontSize: { xs: '0.95rem', md: '1rem' } }}>
                          Priority Management
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', fontSize: { xs: '0.85rem', md: '0.9rem' } }}>
                          Rate and organize brands by importance
                        </Typography>
                      </Box>
                      
                      <Box sx={{ 
                        p: { xs: 3, md: 4 }, 
                        backgroundColor: '#ffffff',
                        borderRadius: { xs: 3, md: 4 },
                        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                        border: '1px solid #e2e8f0',
                        textAlign: 'center'
                      }}>
                        <PaletteIcon sx={{ fontSize: { xs: 32, md: 40 }, color: feature.color, mb: { xs: 1.5, md: 2 } }} />
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 1, fontSize: { xs: '0.95rem', md: '1rem' } }}>
                          Brand Identity
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', fontSize: { xs: '0.85rem', md: '0.9rem' } }}>
                          Comprehensive brand information management
                        </Typography>
                      </Box>
                    </>
                  )}
                  
                  {index === 1 && (
                    <>
                      <Box sx={{ 
                        p: { xs: 3, md: 4 }, 
                        backgroundColor: '#ffffff',
                        borderRadius: { xs: 3, md: 4 },
                        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                        border: '1px solid #e2e8f0',
                        textAlign: 'center'
                      }}>
                        <TimelineIcon sx={{ fontSize: { xs: 32, md: 40 }, color: feature.color, mb: { xs: 1.5, md: 2 } }} />
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 1, fontSize: { xs: '0.95rem', md: '1rem' } }}>
                          Performance Tracking
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', fontSize: { xs: '0.85rem', md: '0.9rem' } }}>
                          Monitor brand success metrics
                        </Typography>
                      </Box>
                      
                      <Box sx={{ 
                        p: { xs: 3, md: 4 }, 
                        backgroundColor: '#ffffff',
                        borderRadius: { xs: 3, md: 4 },
                        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                        border: '1px solid #e2e8f0',
                        textAlign: 'center'
                      }}>
                        <TrendingUpIcon sx={{ fontSize: { xs: 32, md: 40 }, color: feature.color, mb: { xs: 1.5, md: 2 } }} />
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 1, fontSize: { xs: '0.95rem', md: '1rem' } }}>
                          Growth Analytics
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', fontSize: { xs: '0.85rem', md: '0.9rem' } }}>
                          Data-driven brand growth insights
                        </Typography>
                      </Box>
                    </>
                  )}
                  
                  {index === 2 && (
                    <>
                      <Box sx={{ 
                        p: { xs: 3, md: 4 }, 
                        backgroundColor: '#ffffff',
                        borderRadius: { xs: 3, md: 4 },
                        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                        border: '1px solid #e2e8f0',
                        textAlign: 'center'
                      }}>
                        <LaunchIcon sx={{ fontSize: { xs: 32, md: 40 }, color: feature.color, mb: { xs: 1.5, md: 2 } }} />
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 1, fontSize: { xs: '0.95rem', md: '1rem' } }}>
                          Brand Launches
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', fontSize: { xs: '0.85rem', md: '0.9rem' } }}>
                          Strategic brand introduction planning
                        </Typography>
                      </Box>
                      
                      <Box sx={{ 
                        p: { xs: 3, md: 4 }, 
                        backgroundColor: '#ffffff',
                        borderRadius: { xs: 3, md: 4 },
                        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                        border: '1px solid #e2e8f0',
                        textAlign: 'center'
                      }}>
                        <PublicIcon sx={{ fontSize: { xs: 32, md: 40 }, color: feature.color, mb: { xs: 1.5, md: 2 } }} />
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 1, fontSize: { xs: '0.95rem', md: '1rem' } }}>
                          Market Positioning
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', fontSize: { xs: '0.85rem', md: '0.9rem' } }}>
                          Strategic brand market placement
                        </Typography>
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  // Image Showcase Component - Large Image Focus
  const ImageShowcase = () => (
    <Box sx={{ py: { xs: 6, md: 12 }, mt: { xs: -6, md: -10 } }}>
      {/* First Image Section - Brand Management */}
      <Box sx={{ mb: { xs: 8, md: 16 } }}>
        {/* Header Section */}
        <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 8 }, px: { xs: 2, md: 4 } }}>
          <Stack direction="row" justifyContent="center" sx={{ mb: { xs: 2, md: 3 } }}>
            <Chip 
              icon={<LocalMallIcon />}
              label="Brand Overview"
              variant="outlined"
              sx={{ 
                borderColor: '#8b5cf6',
                color: '#8b5cf6',
                fontWeight: 500,
                px: { xs: 2, md: 3 },
                py: { xs: 0.75, md: 1 },
                fontSize: { xs: '0.9rem', md: '1rem' },
                '& .MuiChip-icon': {
                  color: '#8b5cf6'
                }
              }}
            />
          </Stack>
          
          <Typography variant="h2" sx={{ 
            fontWeight: 500,
            color: '#1e293b',
            mb: { xs: 2.5, md: 4 },
            fontSize: { xs: '1.75rem', md: '3.5rem' },
            maxWidth: '800px',
            mx: 'auto',
            px: { xs: 1, md: 0 }
          }}>
            Manage Your{' '}
            <Box component="span" sx={{ color: '#8b5cf6' }}>Brand Portfolio</Box>
          </Typography>
          
          <Typography variant="h6" sx={{ 
            color: '#64748b',
            fontSize: { xs: '1rem', md: '1.3rem' },
            maxWidth: '600px',
            mx: 'auto',
            lineHeight: 1.6,
            px: { xs: 2, md: 0 }
          }}>
            Get complete control over your brand ecosystem with comprehensive management tools
          </Typography>
        </Box>

        {/* Large Image Display */}
        <Box sx={{ 
          maxWidth: '1200px',
          mx: 'auto',
          px: { xs: 2, md: 4 },
          mb: { xs: 4, md: 8 }
        }}>
          <Box sx={{
            position: 'relative',
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
            border: '1px solid #e2e8f0'
          }}>
            <img 
              src={brandImage}
              alt="Brand Management Overview"
              style={{ 
                maxWidth: '100%', 
                height: 'auto',
                display: 'block'
              }}
            />
          </Box>
        </Box>

        {/* Feature Grid Below Image */}
        <Box sx={{ 
          maxWidth: '1200px', 
          mx: 'auto', 
          px: { xs: 2, md: 4 },
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 3, md: 4 },
          alignItems: 'stretch'
        }}>
          {[
            { 
              icon: StarIcon, 
              title: 'Priority Rating',
              desc: 'Set and track brand priorities with our intuitive 5-star rating system'
            },
            { 
              icon: VisibilityIcon, 
              title: 'Brand Details',
              desc: 'Comprehensive brand information including descriptions and website links'
            },
            { 
              icon: GroupIcon, 
              title: 'Portfolio View',
              desc: 'Organize and view all your brands in a structured portfolio layout'
            }
          ].map((item, idx) => (
            <Box key={idx} sx={{ flex: 1, width: '100%' }}>
              <Box sx={{ 
                textAlign: 'center',
                p: { xs: 3, md: 4 },
                backgroundColor: '#ffffff',
                borderRadius: { xs: 3, md: 4 },
                border: '1px solid #e2e8f0',
                boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                height: '100%',
                '&:hover': {
                  boxShadow: '0 8px 40px rgba(139, 92, 246, 0.12)',
                  transform: 'translateY(-4px)',
                  transition: 'all 0.3s ease'
                }
              }}>
                <Box sx={{
                  width: { xs: 56, md: 64 },
                  height: { xs: 56, md: 64 },
                  borderRadius: '50%',
                  backgroundColor: alpha('#8b5cf6', 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: { xs: 2, md: 3 }
                }}>
                  <item.icon sx={{ fontSize: { xs: 28, md: 32 }, color: '#8b5cf6' }} />
                </Box>
                <Typography variant="h6" sx={{ 
                  fontWeight: 500,
                  color: '#1e293b',
                  mb: { xs: 1.5, md: 2 },
                  fontSize: { xs: '1.05rem', md: '1.2rem' }
                }}>
                  {item.title}
                </Typography>
                <Typography variant="body2" sx={{ 
                  color: '#64748b',
                  lineHeight: 1.6,
                  fontSize: { xs: '0.95rem', md: '1rem' }
                }}>
                  {item.desc}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>


      {/* Floating Stats Cards */}
      <Box sx={{ position: 'relative', mt: { xs: 4, md: -8 } }}>
        <Box sx={{ 
          maxWidth: '1000px', 
          mx: 'auto', 
          px: { xs: 2, md: 4 },
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 3, md: 4 },
          alignItems: 'stretch'
        }}>
          {[
            { icon: LocalMallIcon, number: '50+', label: 'Brands Managed', color: '#8b5cf6' },
            { icon: GrowthIcon, number: '85%', label: 'Brand Growth', color: '#a855f7' },
            { icon: StarIcon, number: '4.8★', label: 'Average Rating', color: '#c084fc' }
          ].map((stat, idx) => (
            <Box key={idx} sx={{ flex: 1, width: '100%' }}>
              <Card sx={{ 
                p: { xs: 3, md: 4 },
                textAlign: 'center',
                backgroundColor: '#ffffff',
                boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                borderRadius: { xs: 3, md: 4 },
                border: '1px solid #e2e8f0',
                transform: { xs: 'translateY(0)', md: 'translateY(-20px)' },
                '&:hover': {
                  transform: { xs: 'translateY(0)', md: 'translateY(-25px)' },
                  boxShadow: '0 25px 50px rgba(0,0,0,0.15)',
                  transition: 'all 0.3s ease'
                }
              }}>
                <Box sx={{ 
                  width: { xs: 64, md: 80 },
                  height: { xs: 64, md: 80 },
                  borderRadius: '50%',
                  backgroundColor: alpha(stat.color, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: { xs: 2, md: 3 }
                }}>
                  <stat.icon sx={{ fontSize: { xs: 32, md: 40 }, color: stat.color }} />
                </Box>
                <Typography variant="h2" sx={{ 
                  fontWeight: 500,
                  color: stat.color,
                  mb: 1,
                  fontSize: { xs: '2.5rem', md: '3rem' }
                }}>
                  {stat.number}
                </Typography>
                <Typography variant="h6" sx={{ 
                  fontWeight: 500,
                  color: '#374151',
                  fontSize: { xs: '1.05rem', md: '1.2rem' }
                }}>
                  {stat.label}
                </Typography>
              </Card>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );

  // CTA Section Component
  const CTASection = () => (
    <Box
      sx={{
        py: { xs: 6, md: 8 },
        backgroundColor: '#8b5cf6',
        color: '#fff',
        textAlign: 'center'
      }}
    >
      <Box sx={{ maxWidth: '600px', mx: 'auto', px: { xs: 2, md: 3 } }}>
        <Typography
          variant="h3"
          sx={{
            fontSize: { xs: '1.8rem', md: '2.2rem' },
            fontWeight: 400,
            mb: { xs: 2, md: 3 },
            color: '#ffffff'
          }}
        >
          Ready to transform your brand management?
        </Typography>
        <Typography
          variant="h6"
          sx={{
            fontSize: { xs: '1rem', md: '1.1rem' },
            fontWeight: 400,
            mb: { xs: 3, md: 4 },
            color: '#ddd6fe'
          }}
        >
          Join companies that have revolutionized their brand portfolio management and accelerated their growth with our comprehensive platform.
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={onNavigateToAuth}
          sx={{
            backgroundColor: '#ffffff',
            color: '#8b5cf6',
            fontWeight: 500,
            px: { xs: 4, md: 5 },
            py: { xs: 1.5, md: 2 },
            textTransform: 'none',
            fontSize: { xs: '1rem', md: '1.1rem' },
            borderRadius: 2,
            border: '2px solid #8b5cf6',
            '&:hover': {
              backgroundColor: '#8b5cf6',
              color: '#ffffff'
            }
          }}
        >
          Start Building Today
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Navigation onNavigateToAuth={onNavigateToAuth} />
      <HeroSection />
      <ScrollingTags />
      <FeaturesOverview />
      <ImageShowcase />
      <CTASection />
      <Footer />
    </Box>
  );
};

export default BrandsLandingPage;